import {
  waitlistEntryListResponseSchema,
  waitlistEntryResponseSchema,
  type CreateWaitlistEntryRequest,
  type ListWaitlistQuery,
  type WaitlistEntry,
  type WaitlistStatus,
} from '@emr/shared';

import { ApiError, apiRequest } from './client.ts';

/**
 * The waitlist surface (`BACKLOG.md` K3), validated against `@emr/shared`'s own response schemas —
 * matching `api/appointments.ts`'s own stated convention. `server/src/scheduling/waitlistRoutes.ts`
 * is the route file these calls are wired against.
 */

export type { WaitlistEntry };

/** `GET /api/waitlist-entries?providerId=&status=` — every entry in the caller's own org, oldest
 * first, optionally narrowed to one provider or one status. */
export async function listWaitlist(
  query: Partial<ListWaitlistQuery> = {},
  signal?: AbortSignal,
): Promise<readonly WaitlistEntry[]> {
  const params = new URLSearchParams();
  if (query.providerId !== undefined) params.set('providerId', query.providerId);
  if (query.status !== undefined) params.set('status', query.status);
  const search = params.toString();
  const response = await apiRequest(
    `/api/waitlist-entries${search === '' ? '' : `?${search}`}`,
    waitlistEntryListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.waitlistEntries;
}

/** `POST /api/waitlist-entries` — add a patient to the waitlist. */
export async function createWaitlistEntry(
  input: CreateWaitlistEntryRequest,
): Promise<WaitlistEntry> {
  const response = await apiRequest('/api/waitlist-entries', waitlistEntryResponseSchema, {
    method: 'POST',
    body: input,
  });
  return response.waitlistEntry;
}

/** `POST /api/waitlist-entries/:id/status` — mark an entry `matched` (rebooked into an opened slot)
 * or `expired`. */
export async function updateWaitlistEntryStatus(
  id: string,
  status: WaitlistStatus,
): Promise<WaitlistEntry> {
  const response = await apiRequest(
    `/api/waitlist-entries/${id}/status`,
    waitlistEntryResponseSchema,
    { method: 'POST', body: { status } },
  );
  return response.waitlistEntry;
}

/** The waitlist-specific error vocabulary, turned into a message a front-desk user can act on —
 * matching `appointments.ts`'s own `appointmentErrorMessage` shape. Waitlist entries share
 * `SCHEDULING_ERROR_CODES` with appointments (`schedulingRequests.ts`'s own header), so most codes
 * below reuse that file's exact wording, adjusted for a waitlist entry rather than a booking. */
export function waitlistErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : 'Something went wrong.';
  }
  switch (error.code) {
    case 'patient_not_found':
      return 'That patient could not be found in this practice.';
    case 'provider_not_found':
      return 'That provider is not a current member of this practice.';
    case 'invalid_status_transition':
      return 'That status change is not allowed from the entry’s current status.';
    case 'not_found':
      return 'That waitlist entry no longer exists.';
    case 'audit_unavailable':
      return 'The change could not be recorded and was not made. Try again in a moment.';
    default:
      return error.message;
  }
}
